import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import {
  SCREEN_PATH,
  propertyConstants,
  DEFAULT_CURRENCY_ID,
  createListingFormTypes,
} from "../../../constants";
import { objectToFormData } from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import ConfirmationPopup from "../../../shared/components/modals/ConfirmationPopup";
import { UseNavigatingAway } from "../../../shared/hooks/use-navigating-away/UseNavigatingAway";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import {
  homeActions,
  systemActions,
  propertyActions,
} from "../../../redux/actions";
import Topbar from "../../../layout/Topbar";
import Footer from "../../../layout/Footer";
import ViewTypes from "./cards/ViewTypes";
import PriceAndArea from "./cards/PriceAndArea";
import PropertyTypes from "./cards/PropertyTypes";
import AdInformation from "./cards/AdInformation";
import PropertyImages from "./cards/PropertyImages";
import ListingTypeCard from "./cards/ListingTypeCard";
import FeaturesModal from "./features-modal/FeaturesModal";
import AddressAndLocation from "./cards/AddressAndLocation";
import FeaturesAndAmenities from "./cards/FeaturesAndAmenities";

const validationSchema = Yup.object().shape({
  listingType: Yup.string().required("Listing Type is required"),
  availability: Yup.number().required("Availability is required"),
  openHouse: Yup.boolean(),
  startDate: Yup.number()
    .nullable()
    .when("openHouse", {
      is: true,
      then: (schema) => schema.required(""),
    }),
  endDate: Yup.number()
    .nullable()
    .when("openHouse", {
      is: true,
      then: (schema) =>
        schema
          .required("")
          .test(
            "is-after-start",
            "End Date must be after Start Date",
            function (value) {
              return (
                !this.parent.startDate ||
                (value && value > this.parent.startDate)
              );
            }
          ),
    }),
  area: Yup.number()
    .required("Area is required")
    .min(1, "Area cannot be negative or zero"),
  areaMeasurementType: Yup.number().required(
    "Area Measurement Type is required"
  ),
  price: Yup.number()
    .required("Price is required")
    .min(1, "Price cannot be negative or zero"),
  depositAmount: Yup.number()
    .required("Deposit Amount is required")
    .min(1, "Deposite Amount cannot be negative or zero"),
  currencyId: Yup.number().required("Currency is required"),
  title: Yup.string()
    .trim()
    .required("Title is required")
    .max(200, "Title must not exceed 200 characters"),
  description: Yup.string()
    .trim()
    .required("Description is required")
    .max(5000, "Description must not exceed 5000 characters"),
  city: Yup.string().trim().required("City is required"),
  addressLine1: Yup.string().trim().required("Address Line 1 is required"),
  addressLine2: Yup.string()
    .trim()
    .nullable()
    .max(200, "Address line 2 must not exceed 200 characters"),
  state: Yup.string().trim().required("State is required"),
  countryId: Yup.number().required("Country is required"),
  propertyTypeIds: Yup.array()
    .of(Yup.number())
    .min(1, "At least one property type is required")
    .max(3, "You can select maximim 3 property types")
    .required("Property Type is required"),
  viewTypeIds: Yup.array()
    .of(Yup.number())
    .min(1, "At least one view type is required")
    .max(3, "You can select maximim 3 view types")
    .required("View Type is required"),
  amenity: Yup.object().shape({
    bedrooms: Yup.string().required("Bedrooms is required"),
    bathrooms: Yup.string().required("Bathrooms is required"),
  }),
  images: Yup.array()
    .of(
      Yup.lazy((value) => {
        // Check if the value is a File object or an object with specific properties
        if (
          value &&
          typeof value === "object" &&
          "id" in value &&
          "url" in value &&
          "type" in value
        ) {
          // Validate object structure
          return Yup.object().shape({
            id: Yup.number(),
            url: Yup.string(),
            type: Yup.number(),
          });
        } else {
          // Validate File object
          return Yup.mixed()
            .required("An image file is required")
            .test(
              "fileType",
              "Only PNG, JPG, and JPEG files are allowed",
              (file) => {
                if (!file) return true; // Allow empty files
                const validTypes = ["image/png", "image/jpeg", "image/jpg"];
                const isValidType = validTypes.includes(file.type);
                const fileName = file.name?.toLowerCase();
                const isJfif = fileName?.endsWith(".jfif");
                
                return isValidType && !isJfif;
              }
            );
        }
      })
    )
    .min(1, "At least one image is required") // Minimum of 1 image
    .max(10, "You can upload maximum 10 images") // Maximum of 10 images
    .required("At least one image is required"), // Ensures the field is present
});

const { TYPE_DETAILS, TYPE_FEATURES } = createListingFormTypes;

function convertFieldsToNumbers(obj) {
  const { amenity } = obj;

  // Convert specified fields to numbers

  if (amenity.kitchens !== undefined) {
    amenity.kitchens = Number(amenity.kitchens);
  }

  if (amenity.otherRooms !== undefined) {
    amenity.otherRooms = Number(amenity.otherRooms);
  }

  if (amenity.staffQuarters !== undefined) {
    amenity.staffQuarters = Number(amenity.staffQuarters);
  }

  if (amenity.storeRooms !== undefined) {
    amenity.storeRooms = Number(amenity.storeRooms);
  }
  obj.area = Number(obj.area);
  obj.depositAmount = Number(obj.depositAmount);
  obj.price = Number(obj.price);
  obj.listingType = Number(obj.listingType);

  return obj;
}

const firstPageErrorKeys = [
  "listingType",
  "availability",
  "startDate",
  "endDate",
  "area",
  "areaMeasurementType",
  "price",
  "depositAmount",
  "title",
  "description",
  "propertyTypeIds",
  "viewTypeIds",
  "addressLine1",
  "addressLine2",
  "countryId",
  "state",
  "city",
];

const CreateListing = (props) => {
  const {
    createProperty,
    updateProperty,
    spinnerActions,
    propertyDetail,
    saveChanges,
    setSaveChanges,
    getPropertyDetailById,
    getAllMustHaveAmmenities,
  } = props;

  const { id } = useParams();
  const navigate = useNavigate();
  const [coverImageId, setCoverImageId] = useState();
  const [deletedImages, setDeletedImages] = useState([]);
  const [formType, setFormType] = useState(TYPE_DETAILS);
  const [coverImageIndex, setCoverImageIndex] = useState(0);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);

  const [showDialogPrompt, confirmNavigation, cancelNavigation] =
    UseNavigatingAway(saveChanges);

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      currencyId: DEFAULT_CURRENCY_ID,
    },
  });

  const changeFormType = async (type) => {
    if (type === TYPE_FEATURES) {
      const isValid = await methods.trigger([
        "listingType",
        "availability",
        "startDate",
        "endDate",
        "currencyId",
        "area",
        "areaMeasurementType",
        "price",
        "depositAmount",
        "title",
        "description",
        "propertyTypeIds",
        "viewTypeIds",
        "addressLine1",
        "addressLine2",
        "countryId",
        "state",
        "city",
      ]);

      if (!isValid) {
        window.scrollTo(0, 0);
        return;
      }
    }
    window.scrollTo(0, 0);
    setFormType(type);
  };

  const handleOpenFeatureModal = () => {
    setOpenFeatureModal(true);
  };

  const handleCloseFeatureModal = () => {
    setOpenFeatureModal(false);
  };

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const onSubmit = (data) => {
    setSaveChanges(false);
    const reqObj = convertFieldsToNumbers(data);

    const filteredImages = reqObj.images.filter((img) => !img.url);

    const updatedCoverImageIndex = filteredImages.findIndex(
      (img) => reqObj.images[coverImageIndex] === img
    );

    const formData = objectToFormData(
      {
        ...reqObj,
        ...(id
          ? {
              deletedImageIds: deletedImages,
              images: filteredImages,
              ...(coverImageId ? { coverImageId } : {}),
            }
          : {}),
        ...(reqObj?.nearbyLocations
          ? {
              nearbyLocations: reqObj.nearbyLocations.map((location) => {
                let tempLocation = location;
                if (tempLocation.key) {
                  delete tempLocation.key;
                }
                return { ...tempLocation };
              }),
            }
          : {}),
      },
      updatedCoverImageIndex,
      reqObj.images[coverImageIndex]?.url ? false : true
    );
    if (id) {
      updateProperty(formData, () => {
        navigate(SCREEN_PATH.MY_LISTING);
      });
    } else {
      createProperty(formData, () => {
        navigate(SCREEN_PATH.MY_LISTING);
      });
    }
  };

  useEffect(() => {
    // Check if any of the specified error keys exist in formState.errors
    const hasErrors = firstPageErrorKeys.some(
      (key) => key in methods.formState.errors
    );

    if (hasErrors) {
      changeFormType(TYPE_DETAILS);
    }
    // eslint-disable-next-line
  }, [methods.formState.errors]);

  useEffect(() => {
    if (Object.entries(propertyDetail).length > 0 && id) {
      methods.reset({
        ...propertyDetail,
        listingType: String(propertyDetail.listingType),
      });
      setCoverImageIndex(
        propertyDetail.images.findIndex((img) => img.type === 1)
      );
      setCoverImageId(propertyDetail.images.find((img) => img.type === 1)?.id);
    }

    // eslint-disable-next-line
  }, [propertyDetail]);

  useEffect(() => {
    if (id) {
      getPropertyDetailById(id);
    }

    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    getAllMustHaveAmmenities();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const subscription = methods.watch(() => {
      setSaveChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <>
      <Topbar />
      <MainContentWrapper>
        {(spinner(propertyConstants.CREATE_PROPERTY) > -1 ||
          spinner(propertyConstants.GET_PROPERTY_DETAIL_BY_ID) > -1) && (
          <MainSpinner />
        )}
        {showDialogPrompt && (
          <ConfirmationPopup
            onCancel={cancelNavigation}
            onConfirm={() => {
              confirmNavigation();
              setSaveChanges(false);
            }}
          />
        )}
        <div className="agent-create-listing">
          <div className="container">
            <div className="create-list-form">
              <div className="create-list-form-heading">
                {id ? "Update Listing" : "Create a New Listing"}
              </div>
              <div className="form-type d-flex justify-content-center">
                <div
                  className={`pointer text-center ${
                    formType === TYPE_DETAILS ? "selected-form-type" : ""
                  }`}
                  onClick={() => changeFormType(TYPE_DETAILS)}
                >
                  Ad Details
                </div>
                <div
                  className={`pointer text-center ${
                    formType === TYPE_FEATURES ? "selected-form-type" : ""
                  }`}
                  onClick={() => changeFormType(TYPE_FEATURES)}
                >
                  Features, Amenities and Photos
                </div>
              </div>
              <FormProvider {...methods}>
                <FeaturesModal
                  show={openFeatureModal}
                  onClose={handleCloseFeatureModal}
                />

                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {formType === TYPE_DETAILS ? (
                    <>
                      <ListingTypeCard />
                      <PriceAndArea />
                      <AdInformation />
                      <PropertyTypes />
                      <ViewTypes />
                      <AddressAndLocation />
                      <div className="d-flex justify-content-center">
                        <RoundedButton
                          cotainerClass="mt-4"
                          className="next-button"
                          onClick={() => changeFormType(TYPE_FEATURES)}
                        >
                          Next
                        </RoundedButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <FeaturesAndAmenities
                        handleOpenFeatureModal={handleOpenFeatureModal}
                      />
                      <PropertyImages
                        coverImageIndex={coverImageIndex}
                        setCoverImageIndex={setCoverImageIndex}
                        setDeletedImages={setDeletedImages}
                        setCoverImageId={setCoverImageId}
                      />
                      <div className="d-flex justify-content-center gap-4">
                        <RoundedButton
                          cotainerClass="mt-4"
                          onClick={() => changeFormType(TYPE_DETAILS)}
                          variant="outlined-blue"
                          className="px-lg-5 py-2"
                        >
                          Go Back
                        </RoundedButton>
                        <RoundedButton
                          cotainerClass="mt-4"
                          className="next-button submit-button"
                          type="submit"
                        >
                          {id ? "Update" : "Submit"}
                        </RoundedButton>
                      </div>
                    </>
                  )}
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </MainContentWrapper>
      <Footer />
    </>
  );
};

const mapStateToProps = (state) => ({
  saveChanges: state.systemReducer.saveChanges,
  spinnerActions: state.systemReducer.spinnerActions,
  propertyDetail: state.propertyReducer.propertyDetail,
});

const mapDispatchToProps = (dispatch) => ({
  createProperty: (data, func) =>
    dispatch(propertyActions.createProperty(data, func)),
  updateProperty: (data, func) =>
    dispatch(propertyActions.updateProperty(data, func)),
  getPropertyDetailById: (id) =>
    dispatch(propertyActions.getPropertyDetailById(id)),
  getAllMustHaveAmmenities: () =>
    dispatch(homeActions.getAllMustHaveAmmenities()),
  setSaveChanges: (flag) => dispatch(systemActions.setSaveChanges(flag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateListing);
